import React, { useMemo, useState } from 'react';
import {
  Box,
  Text,
  Loader,
  Alert,
  Paper,
  Tabs,
  Card,
  Group,
  Stack,
  Anchor,
  Table,
  ScrollArea,
  ActionIcon,
  TextInput,
} from '@mantine/core';
import { IconSearch, IconDownload, IconPrinter } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

const SalesPerformanceScorecardBusinessGrowthQtyDetail = () => {
  const { ccode, mode } = useParams();
  const [activeTab, setActiveTab] = useState(mode);
  const navigate = useNavigate();

  // State for pop-ups and search
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Clear handlers for proper dialog closing
  const handleCloseMembership = () => setOpenMembership(false);
  const handleCloseSummaryQuery = () => setOpenSummaryQuery(false);
  const handleCloseDetailsQuery = () => setOpenDetailsQuery(false);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date)) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Compute the date range based on the active mode.
  const computeDateRange = (mode) => {
    const now = new Date();
    let start;
    switch (mode) {
      case 'mtd': // Month-to-Date: from the start of the month
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'qtd': { // Quarter-to-Date: from the start of the current quarter
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      }
      case 'ytd': // Year-to-Date: from January 1st
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
    }
    return {
      from: encodeURIComponent(start.toISOString()),
      to: encodeURIComponent(now.toISOString()),
    };
  };

  // Memoize the date range so it only recalculates when activeTab changes.
  const { from, to } = useMemo(() => computeDateRange(activeTab), [activeTab]);

  // Build the API endpoint using the computed date range.
  const endpoint = `${config.apiBaseUrl}/sales/scorecard/${ccode}/newBusinessGrowthQtyDetails?from=${from}&to=${to}`;
  const { data: salesPersonName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  const { data, isLoading, error } = useAuthenticatedData(endpoint);

  const { rows, filteredRows } = useMemo(() => {
    if (!data) return { rows: [], filteredRows: [] };

    const rows = data
      .map((item) => ({
        id: item.id,
        accountName: item.accountName,
        accountNumber: item.accountNumber,
        marketSegment: item.marketSegment,
        colorCategory: item.colorCategory,
        salesOrderDocNo: item.salesOrderDocNo,
        salesOrderDocTotal: item.salesOrderDocTotal,
        eventDate: item.eventDate,
      }))
      .sort((a, b) => a.accountName.localeCompare(b.accountName));

    // Filter rows based on search term
    const filteredRows = rows.filter(row =>
      !searchTerm ||
      row.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.accountNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.marketSegment.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.salesOrderDocNo.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return { rows, filteredRows };
  }, [data, searchTerm]);

  const handleTabChange = (value) => {
    navigate(`/sales/salesperson/${ccode}/scorecard/new-business-growth-qty/${value}`);
    setActiveTab(value);
  };

  const getRowColor = (colorCategory) => {
    switch (colorCategory?.toLowerCase()) {
      case 'red': return '#D88C8C';
      case 'green': return '#73A57D';
      case 'yellow': return '#D1A752';
      default: return undefined;
    }
  };

  const exportToCSV = () => {
    if (!filteredRows.length) return;

    const headers = ['Account', 'Account Number', 'Market Segment', 'Date', 'Pentagon SO doc no', 'Pentagon SO doc total'];
    const csvContent = [
      headers.join(','),
      ...filteredRows.map(row => [
        `"${row.accountName}"`,
        `"${row.accountNumber}"`,
        `"${row.marketSegment}"`,
        `"${formatDate(row.eventDate)}"`,
        `"${row.salesOrderDocNo}"`,
        row.salesOrderDocTotal
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'growth-qty-details.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading || isLoadingName) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  return (
    <Paper shadow="md" radius="md"  >
      <Stack gap="xs">
        {/* Header Section with Title and Helper Links */}
        <Box>
          <Text
            component="h1"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              color: '#FDB813',
              fontStyle: 'italic',
              fontSize: '1.5rem',
              marginBottom: 0,
              textAlign: 'left'
            }}
          >
            Growth Qty Detail - {salesPersonName}
          </Text>

          {/* Technical options: small links */}
          <Group gap="xs" mb="sm">


            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-individual-mtd-customer-growth-quantity',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Summary Query
            </Anchor>
            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-individual-scorecard-customer-growth-qty-details',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Details Query
            </Anchor>
          </Group>
        </Box>

        {/* Info Card - single card, tighter spacing */}
        <Box style={{ marginBottom: '0.5rem' }}>
          <Card withBorder radius="md" style={{ width: '100%' }}>
            <Text size="sm" fw={500} mb="xs">
              About the Data
            </Text>
            <Text size="xs" c="dimmed">
              This shows all customers that were dead when {salesPersonName} sold to them, regardless of who the account might be assigned to.
              Currently you're seeing all the customers {salesPersonName} sold to {mode.toUpperCase()} that did not buy ANYTHING from us last year.
            </Text>
          </Card>
        </Box>

        {/* Tabs - compact */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          styles={(theme) => ({
            tab: {
              color: theme.colorScheme === 'dark' ? '#b3b3b3' : '#666666',
              '&[data-active]': {
                color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
                fontWeight: 'bold',
              },
              padding: '6px 12px',
            },
          })}
        >
          <Tabs.List>
            <Tabs.Tab value="mtd">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="qtd">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="ytd">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Toolbar - reduced spacing */}
        <Group justify="space-between" mb="xs">
          <TextInput
            placeholder="Search accounts, numbers, or documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size={16} />}
            style={{ width: '350px' }}
            size="sm"
          />
          <Group gap="xs">
            <ActionIcon
              variant="default"
              onClick={exportToCSV}
              title="Export to CSV"
              size="sm"
            >
              <IconDownload size={16} />
            </ActionIcon>
            <ActionIcon
              variant="default"
              onClick={() => window.print()}
              title="Print"
              size="sm"
            >
              <IconPrinter size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Data Table - compact height */}
        <Paper withBorder style={{ height: '500px' }}>
          <ScrollArea style={{ height: '100%' }}>
            <Table striped highlightOnHover size="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '200px' }}>Account</Table.Th>
                  <Table.Th style={{ width: '120px' }}>Account Number</Table.Th>
                  <Table.Th style={{ width: '150px' }}>Market Segment</Table.Th>
                  <Table.Th style={{ width: '100px' }}>Date</Table.Th>
                  <Table.Th style={{ width: '150px' }}>Pentagon SO doc no</Table.Th>
                  <Table.Th style={{ width: '150px' }}>Pentagon SO doc total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredRows.map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{
                      backgroundColor: getRowColor(row.colorCategory),
                    }}
                  >
                    <Table.Td>
                      <Text size="xs" fw={500} c="dark">
                        {row.accountName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dark">
                        {row.accountNumber}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dark">
                        {row.marketSegment}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dark">
                        {formatDate(row.eventDate)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dark">
                        {row.salesOrderDocNo}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fw={600} c="dark">
                        {formatCurrency(row.salesOrderDocTotal)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>

        {/* Results count - minimal spacing */}
        <Text size="xs" c="dimmed">
          Showing {filteredRows.length} result{filteredRows.length !== 1 ? 's' : ''}
        </Text>
      </Stack>

      {/* Pop-up dialogs */}
      <MembershipPopup open={openMembership} onClose={handleCloseMembership} />

      <SQLEditorPopup
        open={openSummaryQuery}
        onClose={handleCloseSummaryQuery}
        title="Summary Query"
        endpoint={`${config.apiBaseUrl}/sales/scorecard/queries/customer-growth-qty-summary`}
      />

      <SQLEditorPopup
        open={openDetailsQuery}
        onClose={handleCloseDetailsQuery}
        title="Details Query"
        endpoint={`${config.apiBaseUrl}/sales/scorecard/queries/customer-growth-qty-detail`}
      />
    </Paper>
  );
};

export default SalesPerformanceScorecardBusinessGrowthQtyDetail;